import React from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '../ui/Table';
import { Button } from '../ui/arc/button';
import mc from '../ui/arc/metric-card.module.css';

// Piezas compartidas por las pestañas de Costos que viven en archivo propio
// (Transferencias, Importaciones, Inversiones, Import TN).

/** Encabezado de pantalla: mismo patrón que Insumos (src/Insumos.tsx). */
export function PageHeader({ title, description, actions }: { title: string; description?: React.ReactNode; actions?: React.ReactNode }) {
    return (
        <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
                <h1 className="text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">{title}</h1>
                {description && <p className="mt-1 max-w-[80ch] text-[length:var(--text-sm)] text-soft">{description}</p>}
            </div>
            {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
    );
}

/** Tarjeta de métrica con valor de TEXTO (montos, fechas, "OK").
 *  Escrita a mano a propósito: el MetricCard de uiarc solo anima números y no acepta prefijo "$".
 *  Usa las MISMAS clases del módulo CSS de metric-card, así se ve idéntica a las otras. */
export function StatCard({ label, value, context, tone, badge, children }: {
    label: string; value?: React.ReactNode; context?: React.ReactNode;
    tone?: 'success' | 'warning' | 'danger'; badge?: React.ReactNode; children?: React.ReactNode;
}) {
    const color = tone === 'success' ? 'text-[var(--success)]' : tone === 'warning' ? 'text-[var(--warning)]' : tone === 'danger' ? 'text-[var(--danger)]' : 'text-ink';
    return (
        <article className={mc.card}>
            <div className={mc.top}><span>{label}</span>{badge}</div>
            {value !== undefined && (
                <div className={cn('truncate text-[length:var(--text-2xl)] font-semibold leading-none tabular-nums tracking-[var(--tracking-display)]', color)}>{value}</div>
            )}
            {children}
            {context && <p>{context}</p>}
        </article>
    );
}

/** Aviso de una línea (error / ok / info), descartable. Mismo estilo que los avisos de Insumos. */
export function Notice({ tone = 'danger', children, onClose, action }: {
    tone?: 'danger' | 'success' | 'warning' | 'info'; children: React.ReactNode; onClose?: () => void; action?: React.ReactNode;
}) {
    const v = tone === 'success' ? '--success' : tone === 'warning' ? '--warning' : tone === 'info' ? '--foreground' : '--danger';
    const Icon = tone === 'success' ? CheckCircle2 : tone === 'info' ? Info : AlertCircle;
    return (
        <div role={tone === 'danger' ? 'alert' : 'status'}
            className="flex items-center gap-2 rounded-[var(--radius-control)] border px-3 py-2 text-[length:var(--text-sm)]"
            style={{
                borderColor: `color-mix(in oklab, var(${v}) 30%, var(--border))`,
                background: `color-mix(in oklab, var(${v}) 8%, var(--card))`,
                color: tone === 'info' ? 'var(--foreground)' : `var(${v})`,
            }}>
            <Icon size={16} className="shrink-0" />
            <span className="min-w-0 flex-1">{children}</span>
            {action}
            {onClose && <Button variant="ghost" size="sm" aria-label="Cerrar" onClick={onClose}><X size={14} /></Button>}
        </div>
    );
}

/** Esqueleto de carga de una pantalla: encabezado + tarjetas + bloque, sin salto de layout. */
export function ScreenSkeleton({ cards = 4 }: { cards?: number }) {
    return (
        <div className="animate-pulse" aria-busy="true" aria-label="Cargando">
            <div className="mb-5 h-8 w-56 rounded-[var(--radius-control)] bg-muted" />
            <div className={cn('mb-5 grid grid-cols-2 gap-3', cards >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3')}>
                {Array.from({ length: cards }).map((_, i) => <div key={i} className="h-[128px] rounded-[var(--radius-surface)] border border-line bg-card" />)}
            </div>
            <div className="h-[360px] rounded-[var(--radius-surface)] border border-line bg-card" />
        </div>
    );
}

/** Contenedor con borde para secciones que no son una DataGrid. */
export function Panel({ title, right, children, className }: { title?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode; className?: string }) {
    return (
        <section className={cn('min-w-0 rounded-[var(--radius-surface)] border border-line bg-card p-4 shadow-xs sm:p-5', className)}>
            {(title || right) && (
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    {title && <h2 className="text-[length:var(--text-base)] font-semibold text-ink">{title}</h2>}
                    {right}
                </div>
            )}
            {children}
        </section>
    );
}
